import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Cloud, FileText, Code, Building2, Package, Truck, ShoppingBag } from 'lucide-react';

/**
 * HeroFlowAnimation - ETL pipeline visualization for the EDI hero.
 *
 * Layout (1200×320 SVG):
 *
 *   ┌─SAP ─┐                                          ┌─ Ford ─┐
 *   │ERP  │ ╲                                      ╱  │ OEM    │
 *   ├─────┤  ╲                                  ╱     ├────────┤
 *   │Oracle│  ╲    ┌────────────────┐         ╱       │ Bosch  │
 *   ├─────┤   ╲───▶│  iConnect      │───────╱         │ Tier 1 │
 *   │CSV  │   ╱    │  Translate +   │         ╲       ├────────┤
 *   ├─────┤  ╱     │  Transport     │          ╲      │ Macy's │
 *   │API  │ ╱      └────────────────┘            ╲    │ Retail │
 *   └─────┘                                       ╲   ├────────┤
 *                                                  ╲  │ Lowe's │
 *                                                   ╲ │ Retail │
 *                                                     └────────┘
 *
 * Animation pieces:
 *   - 4 source boxes on the left (SAP/ERP, Oracle, CSV/Files, API)
 *   - 4 destination boxes on the right (OEM, Tier 1, Retail x 2)
 *   - Curved bezier paths from each source to the iConnect hub
 *   - Curved bezier paths from the hub to each destination
 *   - Sky-blue particles flowing along each path (via SMIL animateMotion)
 *   - Pulsing iConnect hub at center
 *   - Document-type label cycling above the SVG
 *   - Periodic transformation flash on the hub
 *
 * Uses SMIL animateMotion which is browser-native and reliable. Note: SMIL
 * pauses when the tab is hidden, so animations only run when the page is in
 * the foreground (this is correct power-saving behavior).
 */

const DOCS = [
  { code: 'PO 850', label: 'Purchase Order', color: '#0EA5E9' },
  { code: '830', label: 'Planning Schedule', color: '#A78BFA' },
  { code: 'INV 810', label: 'Invoice', color: '#37ACE4' },
  { code: 'ASN 856', label: 'Ship Notice', color: '#7DD3FC' },
  { code: '997 ACK', label: 'Acknowledgment', color: '#10B981' },
];

const SOURCES = [
  { id: 's1', label: 'ERP / SAP', sub: 'IDoc · BAPI', y: 50, Icon: Database },
  { id: 's2', label: 'Oracle / NetSuite', sub: 'API · CSV', y: 120, Icon: Cloud },
  { id: 's3', label: 'Files', sub: 'X12 · EDIFACT', y: 200, Icon: FileText },
  { id: 's4', label: 'Custom API', sub: 'REST · XML', y: 270, Icon: Code },
];

// Each destination cycles through several real client sets (they "pop" in one
// after another) so the diagram feels alive. `sub` is the static first set
// used by the reduced-motion / mobile fallbacks.
const DESTS = [
  {
    id: 'd1', label: 'OEMs', y: 50, Icon: Building2,
    sub: 'Ford · GM · Toyota',
    subs: ['Ford · GM · Toyota', 'Honda · BMW · Nissan', 'Stellantis · VW', 'Mercedes · Hyundai'],
  },
  {
    id: 'd2', label: 'Tier 1', y: 120, Icon: Package,
    sub: 'Bosch · Magna',
    subs: ['Bosch · Magna', 'Denso · Aptiv', 'Continental · ZF', 'Faurecia · Lear', 'Valeo · Yazaki'],
  },
  {
    id: 'd3', label: 'Industrial', y: 200, Icon: Truck,
    sub: '3M · Caterpillar',
    subs: ['3M · Caterpillar', 'John Deere · Whirlpool', 'Cummins · Eaton'],
  },
  {
    id: 'd4', label: 'Retail', y: 270, Icon: ShoppingBag,
    sub: "Macy's · Lowe's",
    subs: ["Macy's · Lowe's", 'Home Depot · Best Buy', 'Amazon · Target', "Walmart · Kohl's"],
  },
];

const VIEW = { w: 1200, h: 360 };
// Hub size bumped to r=105 (was 82) so all 3 lines (iConnect / TRANSLATE /
// TRANSFORM · TRANSPORT) sit comfortably inside the circle - the longer
// "TRANSFORM" word needs the extra room.
const HUB = { x: 600, y: 175, r: 105 };
// Box geometry - wider than v1 (was 130) so the company-list sublabels
// ("Ford · GM · Toyota", "3M · Caterpillar", etc.) don't overflow the card edge.
const BOX_W = 200;
const BOX_H = 56;
const SOURCE_CX = 105; // center-x of source boxes (left margin shifted to fit wider boxes)
const DEST_CX = 1095; // center-x of dest boxes
const SOURCE_X_PATH = SOURCE_CX + BOX_W / 2; // where curves start (right edge of source box)
const DEST_X_PATH = DEST_CX - BOX_W / 2;

const HeroFlowAnimation = () => {
  const [docIndex, setDocIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pulseHub, setPulseHub] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [destCycle, setDestCycle] = useState(0);
  const [inView, setInView] = useState(true);
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  // Reduced motion check
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Mobile detection - at narrow viewports the full ETL diagram is too dense
  // to read. We swap to a simplified vertical layout below 768px.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Pause SMIL + timers when the diagram scrolls out of view (it sits at the
  // top of a long page, so this saves CPU while the rest of the page is read).
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '120px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Pause/resume the SVG's native SMIL particle animations with view state.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof svg.pauseAnimations !== 'function') return;
    if (inView) svg.unpauseAnimations();
    else svg.pauseAnimations();
  }, [inView]);

  // Cycle the document label every ~3.2s
  useEffect(() => {
    if (reducedMotion || !inView) return;
    const t = setInterval(() => {
      setDocIndex((i) => (i + 1) % DOCS.length);
    }, 3200);
    return () => clearInterval(t);
  }, [reducedMotion, inView]);

  // Cycle the destination client names every ~2.8s (different clients pop in).
  useEffect(() => {
    if (reducedMotion || !inView) return;
    const t = setInterval(() => setDestCycle((c) => c + 1), 2800);
    return () => clearInterval(t);
  }, [reducedMotion, inView]);

  // Hub pulse trigger every ~3.2s synced to doc cycle
  useEffect(() => {
    if (reducedMotion || !inView) return;
    const t = setInterval(() => {
      setPulseHub(true);
      setTimeout(() => setPulseHub(false), 600);
    }, 3200);
    return () => clearInterval(t);
  }, [reducedMotion, inView]);

  const doc = DOCS[docIndex];

  if (reducedMotion) {
    return <ReducedMotionFallback />;
  }

  // Mobile view: simplified vertical layout with readable text.
  if (isMobile) {
    return <MobileFlow doc={doc} pulseHub={pulseHub} />;
  }

  // Fluid container: pipeline expands to fill the hero width on big screens
  // (capped at 1600px). Below that uses a sensible default.
  return (
    <div
      ref={containerRef}
      className="w-full mx-auto mt-10 lg:mt-14 px-4"
      style={{ maxWidth: 'min(96vw, 1600px)' }}
    >
      <div
        className="relative"
        role="img"
        aria-label="ETL pipeline visualization: data flowing from multiple sources through iConnect to trading partners."
      >
        {/* Live caption above the diagram */}
        <AnimatePresence mode="wait">
          <motion.div
            key={docIndex}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.3 }}
            className="absolute -top-8 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white border border-border-subtle shadow-soft text-[11px] font-mono uppercase tracking-widest text-text-secondary whitespace-nowrap z-10"
            style={{ color: doc.color }}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: doc.color }} />
            live: {doc.code} · {doc.label.toLowerCase()}
          </motion.div>
        </AnimatePresence>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW.w} ${VIEW.h}`}
          className="w-full h-auto"
          aria-hidden="true"
        >
          <defs>
            {/* Gradient for the hub disc */}
            <linearGradient id="hubGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#37ACE4" />
              <stop offset="100%" stopColor="#0EA5E9" />
            </linearGradient>

            {/* Soft glow filter */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="glow-strong" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="10" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            {/* Path definitions for particles to follow.
               Each source curves into the hub; each destination curves out. */}
            {SOURCES.map((s) => (
              <path
                key={`path-in-${s.id}`}
                id={`path-in-${s.id}`}
                d={`M ${SOURCE_X_PATH} ${s.y} C ${SOURCE_X_PATH + 200} ${s.y}, ${HUB.x - 200} ${HUB.y}, ${HUB.x - HUB.r} ${HUB.y}`}
                fill="none"
              />
            ))}
            {DESTS.map((d) => (
              <path
                key={`path-out-${d.id}`}
                id={`path-out-${d.id}`}
                d={`M ${HUB.x + HUB.r} ${HUB.y} C ${HUB.x + 200} ${HUB.y}, ${DEST_X_PATH - 200} ${d.y}, ${DEST_X_PATH} ${d.y}`}
                fill="none"
              />
            ))}
          </defs>

          {/* Visible curved rails - drawn for each path, dashed + flowing */}
          {SOURCES.map((s) => (
            <use
              key={`rail-in-${s.id}`}
              href={`#path-in-${s.id}`}
              stroke="rgba(55, 172, 228, 0.18)"
              strokeWidth="1.5"
              fill="none"
            />
          ))}
          {DESTS.map((d) => (
            <use
              key={`rail-out-${d.id}`}
              href={`#path-out-${d.id}`}
              stroke="rgba(55, 172, 228, 0.18)"
              strokeWidth="1.5"
              fill="none"
            />
          ))}

          {/* Animated dashed flow rails on top - gives a continuous "data flow" appearance */}
          {SOURCES.map((s) => (
            <use
              key={`flow-in-${s.id}`}
              href={`#path-in-${s.id}`}
              stroke="#37ACE4"
              strokeWidth="1.5"
              fill="none"
              className="flow-rail"
              style={{ strokeOpacity: 0.7 }}
            />
          ))}
          {DESTS.map((d) => (
            <use
              key={`flow-out-${d.id}`}
              href={`#path-out-${d.id}`}
              stroke="#37ACE4"
              strokeWidth="1.5"
              fill="none"
              className="flow-rail-slow"
              style={{ strokeOpacity: 0.7 }}
            />
          ))}

          {/* Particles traveling along each inbound path. SMIL animateMotion +
             mpath references the path. Multiple particles per rail with
             staggered delays for a continuous "stream" feel. */}
          {SOURCES.map((s, srcIdx) =>
            [0, 0.7, 1.4, 2.1].map((delay, i) => (
              <Particle
                key={`pin-${s.id}-${i}`}
                pathId={`path-in-${s.id}`}
                color={DOCS[(srcIdx + i) % DOCS.length].color}
                delay={delay}
                duration={2.8}
              />
            ))
          )}
          {DESTS.map((d, dstIdx) =>
            [0.35, 1.05, 1.75, 2.45].map((delay, i) => (
              <Particle
                key={`pout-${d.id}-${i}`}
                pathId={`path-out-${d.id}`}
                color={DOCS[(dstIdx + i + 1) % DOCS.length].color}
                delay={delay}
                duration={2.8}
              />
            ))
          )}

          {/* Source boxes (left column) */}
          {SOURCES.map((s) => (
            <SourceBox key={s.id} {...s} />
          ))}

          {/* Destination boxes (right column) - client sub-labels cycle,
             offset per box so different clients show at the same time. */}
          {DESTS.map((d, i) => (
            <DestBox
              key={d.id}
              {...d}
              sub={d.subs[(destCycle + i) % d.subs.length]}
            />
          ))}

          {/* === iConnect HUB (the center) === */}
          <g>
            {/* Outer slow halo (always pulsing softly) */}
            <motion.circle
              cx={HUB.x}
              cy={HUB.y}
              r={HUB.r + 30}
              fill="rgba(55, 172, 228, 0.08)"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
            />
            {/* Mid halo */}
            <motion.circle
              cx={HUB.x}
              cy={HUB.y}
              r={HUB.r + 12}
              fill="rgba(55, 172, 228, 0.18)"
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.5, 0.85, 0.5],
              }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
            />
            {/* Pulse burst (every doc cycle) */}
            <AnimatePresence>
              {pulseHub && (
                <motion.circle
                  cx={HUB.x}
                  cy={HUB.y}
                  r={HUB.r}
                  fill="none"
                  stroke="#37ACE4"
                  strokeWidth="2"
                  initial={{ scale: 1, opacity: 0.8 }}
                  animate={{ scale: 2.2, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
                />
              )}
            </AnimatePresence>

            {/* Hub disc */}
            <circle
              cx={HUB.x}
              cy={HUB.y}
              r={HUB.r}
              fill="url(#hubGrad)"
              filter="url(#glow-strong)"
            />

            {/* Hub labels - sized + spaced to all sit comfortably INSIDE
               the circle (radius 105). Vertical layout, top-down:
                 -16  iConnect (32px display)
                 +12  TRANSLATE (13px mono)
                 +30  TRANSFORM · TRANSPORT (13px mono)
               Bottom of last line ≈ y + 32, well within the r=105 circle. */}
            <text
              x={HUB.x}
              y={HUB.y - 14}
              textAnchor="middle"
              fontFamily="Geist, sans-serif"
              fontSize="32"
              fontWeight="800"
              fill="#FFFFFF"
              style={{ letterSpacing: '-0.02em' }}
            >
              iConnect
            </text>
            <text
              x={HUB.x}
              y={HUB.y + 12}
              textAnchor="middle"
              fontFamily="Geist Mono, monospace"
              fontSize="13"
              fontWeight="600"
              fill="#FFFFFF"
              opacity="0.92"
              style={{ letterSpacing: '0.12em' }}
            >
              TRANSLATE
            </text>
            <text
              x={HUB.x}
              y={HUB.y + 30}
              textAnchor="middle"
              fontFamily="Geist Mono, monospace"
              fontSize="13"
              fontWeight="600"
              fill="#FFFFFF"
              opacity="0.92"
              style={{ letterSpacing: '0.12em' }}
            >
              TRANSFORM · TRANSPORT
            </text>
          </g>
        </svg>
      </div>

      {/* Legend below */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-text-muted">
        <LegendDot color="#0EA5E9" label="PO 850" />
        <LegendDot color="#A78BFA" label="830" />
        <LegendDot color="#37ACE4" label="INV 810" />
        <LegendDot color="#7DD3FC" label="ASN 856" prominent note="most common" />
        <LegendDot color="#10B981" label="997 ACK" />
        <span className="text-border-subtle hidden sm:inline">•</span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-accent-primary">
          Real-time EDI pipeline
        </span>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------- */
/*  Helpers                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Particle - a glowing dot that follows an SVG path using SMIL animateMotion.
 *
 * `pathId` is the id of a <path> in <defs>. `color` lets each stream have its
 * own tint. `delay` staggers particles along the same path.
 */
const Particle = ({ pathId, color, delay, duration }) => (
  <circle
    r="2.5"
    fill={color}
    opacity="0"
    style={{ filter: `drop-shadow(0 0 4px ${color})` }}
  >
    <animateMotion
      dur={`${duration}s`}
      begin={`${delay}s`}
      repeatCount="indefinite"
      rotate="auto"
    >
      <mpath href={`#${pathId}`} />
    </animateMotion>
    <animate
      attributeName="opacity"
      values="0;1;1;0"
      keyTimes="0;0.1;0.85;1"
      dur={`${duration}s`}
      begin={`${delay}s`}
      repeatCount="indefinite"
    />
    <animate
      attributeName="r"
      values="2;3;3;2"
      keyTimes="0;0.1;0.85;1"
      dur={`${duration}s`}
      begin={`${delay}s`}
      repeatCount="indefinite"
    />
  </circle>
);

/**
 * SourceBox / DestBox - cards on the left/right of the ETL diagram.
 *
 * Implementation note (May 2026): the entire card (background + icon + text)
 * is rendered inside a single `<foreignObject>` so HTML/CSS can handle the
 * layout and text overflow properly. The previous SVG `<text>` approach
 * caused company-list sublabels ("Ford · GM · Toyota", "3M · Caterpillar")
 * to bleed past the card's right edge because SVG text doesn't wrap or clip
 * by itself. With foreignObject, the inner div uses `overflow: hidden` and
 * `text-overflow: ellipsis` and we get correct constrained layout.
 */
const SourceBox = ({ y, label, sub, Icon }) => (
  <CardBox
    cx={SOURCE_CX}
    y={y}
    label={label}
    sub={sub}
    Icon={Icon}
    iconGradient="linear-gradient(135deg, #37ACE4, #0EA5E9)"
    align="left"
  />
);

const DestBox = ({ y, label, sub, Icon }) => (
  <CardBox
    cx={DEST_CX}
    y={y}
    label={label}
    sub={sub}
    Icon={Icon}
    iconGradient="linear-gradient(135deg, #0EA5E9, #005B7E)"
    align="right"
  />
);

const CardBox = ({ cx, y, label, sub, Icon, iconGradient, align }) => (
  <foreignObject
    x={cx - BOX_W / 2}
    y={y - BOX_H / 2}
    width={BOX_W}
    height={BOX_H}
  >
    <div
      xmlns="http://www.w3.org/1999/xhtml"
      style={{
        width: '100%',
        height: '100%',
        background: '#FFFFFF',
        border: '1.5px solid #E2E8F0',
        borderRadius: 10,
        boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: align === 'right' ? 'row-reverse' : 'row',
        alignItems: 'center',
        gap: 10,
        padding: '0 12px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Icon tile */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: iconGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={18} color="#FFFFFF" strokeWidth={2.4} />
      </div>
      {/* Text - flex:1 + min-width:0 lets ellipsis overflow work */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: align === 'right' ? 'right' : 'left',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#0F172A',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.01em',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 10,
            fontFamily: '"Geist Mono", monospace',
            fontWeight: 500,
            color: '#94A3B8',
            lineHeight: 1.3,
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 2,
            minHeight: 13,
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={sub}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{ display: 'inline-block' }}
            >
              {sub}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>
  </foreignObject>
);

/** Color-dot legend item */
const LegendDot = ({ color, label, prominent, note }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className="w-2 h-2 rounded-full"
      style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
    />
    <span
      className={`font-mono text-[11px] uppercase tracking-widest ${
        prominent ? 'font-semibold text-text-primary' : ''
      }`}
    >
      {label}
    </span>
    {note && (
      <span className="text-[10px] normal-case tracking-normal text-text-muted">
        · {note}
      </span>
    )}
  </span>
);

/**
 * MobileFlow - simplified vertical EDI flow for narrow viewports.
 *
 * The full ETL diagram has tiny SVG text that's unreadable at <768px.
 * This version uses HTML cards stacked vertically (Sources → iConnect → Dest)
 * with native font sizes that scale properly with viewport, plus a flowing
 * vertical line.
 */
const MobileFlow = ({ doc }) => (
  <div className="w-full max-w-md mx-auto mt-10 px-4">
    {/* Live caption */}
    <AnimatePresence mode="wait">
      <motion.div
        key={doc.code}
        initial={{ opacity: 0, y: -3 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 3 }}
        transition={{ duration: 0.3 }}
        className="mx-auto mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-border-subtle shadow-soft text-[11px] font-mono uppercase tracking-widest"
        style={{ color: doc.color }}
      >
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: doc.color }} />
        live: {doc.code} · {doc.label.toLowerCase()}
      </motion.div>
    </AnimatePresence>

    <div className="flex flex-col items-center gap-3 relative">
      {/* Sources card */}
      <MobileTier
        title="Your Systems"
        items={['ERP / SAP', 'Oracle / NetSuite', 'Files', 'Custom API']}
        accent="from-sky-300 to-sky-400"
      />

      {/* Down arrow with flowing dashes */}
      <MobileConnector />

      {/* iConnect hub */}
      <div className="w-full rounded-2xl bg-gradient-sky shadow-glow-sky p-5 text-center text-white relative overflow-hidden">
        <div className="font-display font-extrabold text-2xl tracking-tight">
          iConnect
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest opacity-90 mt-0.5">
          Translate · Transform · Transport
        </div>
      </div>

      <MobileConnector />

      {/* Destinations card */}
      <MobileTier
        title="Trading Partners"
        items={['Automotive OEMs', 'Tier 1 Suppliers', 'Industrial', 'Retail']}
        accent="from-sky-500 to-cyan-600"
      />
    </div>

    {/* Legend */}
    <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-text-muted">
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9]" />
        PO 850
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]" />
        830
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#37ACE4]" />
        INV 810
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#7DD3FC]" />
        <span className="font-semibold text-text-primary">ASN 856</span>
        <span className="text-[10px] text-text-muted">· most common</span>
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
        997 ACK
      </span>
    </div>
  </div>
);

const MobileTier = ({ title, items }) => (
  <div className="w-full card-elevated p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest text-accent-deep font-bold mb-2">
      {title}
    </div>
    <div className="grid grid-cols-2 gap-1.5">
      {items.map((item) => (
        <div
          key={item}
          className="text-xs font-semibold text-text-primary px-2 py-1.5 rounded-md bg-bg-soft border border-border-subtle"
        >
          {item}
        </div>
      ))}
    </div>
  </div>
);

const MobileConnector = () => (
  <div className="flex flex-col items-center" aria-hidden>
    {/* 3 small dashes that flow down - pure CSS animation on opacity */}
    <div className="flex flex-col gap-1">
      <span
        className="w-1 h-1 rounded-full bg-accent-primary"
        style={{ animation: 'mobile-flow 1.5s ease-in-out infinite' }}
      />
      <span
        className="w-1 h-1 rounded-full bg-accent-primary"
        style={{ animation: 'mobile-flow 1.5s ease-in-out 0.2s infinite' }}
      />
      <span
        className="w-1 h-1 rounded-full bg-accent-primary"
        style={{ animation: 'mobile-flow 1.5s ease-in-out 0.4s infinite' }}
      />
    </div>
  </div>
);

/**
 * ReducedMotionFallback - static three-stage layout for users with
 * prefers-reduced-motion. No animation, just a clean diagram.
 */
const ReducedMotionFallback = () => (
  <div className="w-full max-w-5xl mx-auto mt-12 px-4">
    <div className="card-elevated p-8 grid grid-cols-3 items-center gap-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-text-muted mb-3">
          Sources
        </div>
        <div className="space-y-2">
          {SOURCES.map((s) => (
            <div key={s.id} className="text-sm font-semibold text-text-primary">
              {s.label}
            </div>
          ))}
        </div>
      </div>
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-gradient-sky shadow-glow-sky mx-auto flex items-center justify-center">
          <span className="text-white font-display font-extrabold">i</span>
        </div>
        <div className="mt-3 text-sm font-bold text-text-primary">iConnect</div>
        <div className="text-xs text-text-muted">VAN</div>
      </div>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-text-muted mb-3 text-right">
          Destinations
        </div>
        <div className="space-y-2 text-right">
          {DESTS.map((d) => (
            <div key={d.id} className="text-sm font-semibold text-text-primary">
              {d.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default HeroFlowAnimation;
